'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { ArrowRight, AlertTriangle } from 'lucide-react';

import { RECAPTCHA_PUBLIC_KEY } from '@/constants';
import {
  generateFullName,
  generateEmailDomain,
} from '@/helpers/fake-data.helpers';
import { delay } from '@/utils';
import useThrottledEffect from '@/hooks/use-throttled-effect';
import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';

import Toast from '@/components/Toast';
import ButtonPlayful from '@/components/ButtonPlayful';
import Spinner from '@/components/Spinner';
import TextLink from '@/components/Link/TextLink';
import ButtonLink from '@/components/Link/ButtonLink';

import LabeledInput from './LabeledInput';
import { FaqContext } from './FaqProvider';

const MSG_KEY = 'contact-message';
type Status = 'idle' | 'submitting' | 'success' | 'error';

function ContactForm(props: React.HTMLAttributes<HTMLDivElement>) {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [message, setMessage] = React.useState('');

  const { relevantMatches, setRelevantMatches } =
    React.useContext(FaqContext);

  const relevantMatchesRef = React.useRef(relevantMatches);
  React.useEffect(() => {
    relevantMatchesRef.current = relevantMatches;
  }, [relevantMatches]);

  const [status, setStatus] = React.useState<Status>('idle');

  const prefersReducedMotion = usePrefersReducedMotion();

  React.useEffect(() => {
    const savedMessageData = window.localStorage.getItem(MSG_KEY);
    if (!savedMessageData) {
      return;
    }

    const { name, email, message } = JSON.parse(savedMessageData);

    setName(name);
    setEmail(email);
    setMessage(message);
  }, []);

  React.useEffect(() => {
    window.localStorage.setItem(
      MSG_KEY,
      JSON.stringify({ name, email, message })
    );
  }, [name, email, message]);

  // Check and see if their message is answered by one of the frequently asked questions
  useThrottledEffect(
    () => {
      async function runEffect() {
        // If they’ve erased their whole message, unset the suggestions
        if (message.trim().length < 3) {
          setRelevantMatches([]);
          return;
        }

        try {
          const response = await fetch(
            '/api/contact-page-answer-relevance',
            {
              method: 'POST',
              body: JSON.stringify({ messageText: message }),
            }
          );

          const json = await response.json();

          // If there are any matches, set them.
          // NOTE: This logic means that if their message *stops* matching a previous match, that stale match will still be shown. This is because in testing, it was annoying for the suggestion to appear/disappear over and over. I'd rather show a stale suggestion than a flickering one.
          if (json.relevantMatches?.length > 0) {
            setRelevantMatches(json.relevantMatches);
          }
        } catch (err) {
          // Ignore errors, since this is a nice-to-have feature.
        }
      }

      runEffect();
    },
    [message],
    1000
  );

  const [namePlaceholder] = React.useState(generateFullName);
  const [emailPlaceholder] = React.useState(() => {
    const name = namePlaceholder.match(/^Dr\./)
      ? 'the-doctor'
      : namePlaceholder.split(' ')[0].toLowerCase();
    const domain = generateEmailDomain();

    return `${name}@${domain}`;
  });

  async function handleSubmit(
    event: React.FormEvent<HTMLFormElement>
  ) {
    event.preventDefault();

    setStatus('submitting');

    const startAt = Date.now();

    // @ts-ignore
    const { grecaptcha } = window;

    if (!grecaptcha) {
      setStatus('error');
      return;
    }

    grecaptcha.enterprise.ready(async () => {
      try {
        const token = await grecaptcha.enterprise.execute(
          RECAPTCHA_PUBLIC_KEY,
          { action: 'CONTACTFORM' }
        );

        const res = await fetch('/api/contact-josh', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name, email, message, token }),
        });

        const duration = Date.now() - startAt;

        // If the submission happens *too* fast, it feels like maybe it didn't work. Wait a minimum amount of time.
        const MIN_TIME = 1000;
        if (duration < MIN_TIME) {
          await delay(MIN_TIME - duration);
        }

        if (res.ok) {
          setStatus('success');
          setName('');
          setEmail('');
          setMessage('');
        } else {
          setStatus('error');
        }
      } catch (err) {
        // If there's a network failure or whatever, we'll end up here.
        setStatus('error');
      }
    });
  }

  return (
    <Wrapper {...props}>
      <Form onSubmit={handleSubmit}>
        <Row>
          <Column>
            <LabeledInput
              required
              autoComplete="given-name"
              label="Preferred name"
              placeholder={namePlaceholder}
              value={name}
              setValue={setName}
              disabled={status === 'submitting'}
            />
          </Column>
          <Column>
            <LabeledInput
              required
              type="email"
              autoComplete="email"
              label="Email address"
              placeholder={emailPlaceholder}
              value={email}
              setValue={setEmail}
              disabled={status === 'submitting'}
            />
          </Column>
        </Row>
        <LabeledInput
          as="textarea"
          label="Message"
          value={message}
          setValue={setMessage}
          disabled={status === 'submitting'}
          style={{
            '--scrollbar-thumb': 'var(--blue)',
            '--scrollbar-bg': 'white',
            paddingBottom: '1.25rem',
          }}
        />

        {relevantMatches.length > 0 && (
          <RelevanceIndicator
            relevantMatches={relevantMatches}
            prefersReducedMotion={prefersReducedMotion}
          />
        )}

        <ButtonPlayful
          textAlign="true-center"
          Icon={status === 'submitting' ? Spinner : ArrowRight}
          iconSide="right"
          boopEffect={{ x: 3 }}
          disabled={status === 'submitting'}
        >
          Send message
        </ButtonPlayful>
      </Form>

      {status === 'success' && (
        <Toast
          type="success"
          title="Message received!"
          handleDismiss={() => setStatus('idle')}
          popTimeout={0}
        >
          Your message has been delivered. Thanks for reaching out!
        </Toast>
      )}
      {status === 'error' && (
        <Toast
          type="error"
          title="Something has gone wrong"
          handleDismiss={() => setStatus('idle')}
          popTimeout={0}
        >
          Unfortunately, your message could not be delivered. Sorry
          about that! Please send your message to{' '}
          <TextLink href="mailto:support@joshwcomeau.com">
            support@joshwcomeau.com
          </TextLink>
          .
        </Toast>
      )}
    </Wrapper>
  );
}

function RelevanceIndicator({
  relevantMatches,
  prefersReducedMotion,
}: {
  relevantMatches: Array<string>;
  prefersReducedMotion: boolean;
}) {
  if (relevantMatches?.length === 0) {
    return null;
  }

  let copy =
    relevantMatches.length === 1 ? (
      <>
        It looks like your message is related to one of the{' '}
        <ButtonLink
          type="button"
          onClick={() => {
            const elem = document.querySelector(
              `[data-faq-slug="${relevantMatches[0]}"]`
            ) as HTMLDetailsElement | null;

            if (!elem) {
              return;
            }

            elem.open = true;

            elem.scrollIntoView({
              behavior: prefersReducedMotion ? 'instant' : 'smooth',
              block: 'start',
            });
          }}
        >
          Frequently Asked Questions
        </ButtonLink>
        . Please give it a quick read, if you haven’t already. ❤️
      </>
    ) : (
      <>
        It looks like your message is related to some of the{' '}
        <strong>Frequently Asked Questions</strong>! The relevant
        questions have been highlighted.
      </>
    );

  return (
    <RelevanceWrapper>
      <AlertTriangle />
      <Column>
        <RelevanceContent>{copy}</RelevanceContent>
        <RelevanceContent>
          If you still have questions afterwards, feel free to reach
          out!
        </RelevanceContent>
      </Column>
    </RelevanceWrapper>
  );
}

const Wrapper = styled.div`
  --red: hsl(0deg 85% 70%);
  --blue: hsl(210deg 85% 65%);
  --blue-darker: hsl(225deg 40% 20%);

  margin-inline: calc(var(--stripe-border-width) * -1);
  background: repeating-linear-gradient(
    45deg,
    var(--red) 0px 15px,
    white 15px 25px,
    var(--blue) 25px 40px,
    white 40px 50px
  );
  padding: var(--stripe-border-width);
  border-radius: 8px;
  border: 2px solid white;
  box-shadow:
    0px 1px 2px hsl(0deg 0% 0% / 0.1),
    0px 2px 4px hsl(0deg 0% 0% / 0.1),
    0px 4px 8px hsl(0deg 0% 0% / 0.1);
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  background: white;
  border-radius: 4px;
  container-type: inline-size;
`;

const Row = styled.div`
  display: flex;
  gap: 16px;

  @container (max-width: 330px) {
    display: block;
  }
`;

const Column = styled.div`
  flex: 1;
`;

const RelevanceWrapper = styled.div`
  --color-primary: var(--color-warning-500);
  display: flex;
  gap: 12px;
  padding: 12px;
  border-radius: 4px;
  background: var(--color-warning-100);
  animation: fadeIn 400ms;

  svg {
    flex-shrink: 0;
    color: var(--color-primary);
  }
`;

const RelevanceContent = styled.p`
  && {
    font-size: 0.875rem;
    margin: 0;
    text-align: left;
    animation: fadeIn 400ms 300ms both;

    &:first-child {
      margin-bottom: 1em;
    }
  }
`;

export default ContactForm;
